import { useMemo, useState } from 'react';
import {
  DndContext,
  closestCenter,
  PointerSensor,
  useSensor,
  useSensors,
  type DragEndEvent,
} from '@dnd-kit/core';
import {
  arrayMove,
  SortableContext,
  useSortable,
  verticalListSortingStrategy,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { useTranslation } from 'react-i18next';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { useCercaUsuaris } from '@/api/activacions';
import { useMissatgeError } from '@/api/errors';
import {
  nomUsuari,
  useCalendari,
  useGeneraRotacio,
  useNoms,
  useRotacions,
  useTorns,
  type Periodicitat,
  type ResultatRotacioDto,
  type RotacioDto,
} from '@/api/planificacio';
import { Icona } from '@/components/Icona';
import { useFormat } from '@/i18n/useFormat';
import { Toolbar } from '@/layout/Toolbar';
import { Avatar, Camp } from '@/pages/operacio/comu';
import {
  avui,
  dataCurta,
  diaClau,
  participants,
  rotacioActiva,
  sumaDies,
  tornsSolapats,
} from './calendari';
import './planificacio.css';

const PERIODICITATS: Periodicitat[] = ['SETMANAL', 'QUINZENAL', 'MENSUAL'];
const PREVI_VISIBLES = 8;

function Participant({
  id,
  nom,
  index,
  total,
  onMou,
  onTreu,
}: {
  id: number;
  nom: string;
  index: number;
  total: number;
  onMou: (de: number, a: number) => void;
  onTreu: () => void;
}) {
  const { t } = useTranslation('planificacio');
  const { attributes, listeners, setNodeRef, transform, transition } = useSortable({ id });
  return (
    <li
      ref={setNodeRef}
      className="list-row"
      data-participant={id}
      style={{ transform: CSS.Transform.toString(transform), transition }}
    >
      <span className="participant__ordre" {...attributes} {...listeners}>
        {index + 1}
      </span>
      <Avatar nom={nom} />
      <span className="list-row__text list-row__label">{nom}</span>
      <span className="participant__accions">
        <button
          type="button"
          className="btn btn--ghost btn--xs"
          aria-label={t('rotacio.puja', { nom })}
          title={t('rotacio.puja', { nom })}
          disabled={index === 0}
          onClick={() => onMou(index, index - 1)}
        >
          <Icona nom="chevron-up" />
        </button>
        <button
          type="button"
          className="btn btn--ghost btn--xs"
          aria-label={t('rotacio.baixa', { nom })}
          title={t('rotacio.baixa', { nom })}
          disabled={index === total - 1}
          onClick={() => onMou(index, index + 1)}
        >
          <Icona nom="chevron-down" />
        </button>
        <button
          type="button"
          className="btn btn--ghost btn--xs c-red"
          aria-label={t('rotacio.treu', { nom })}
          title={t('rotacio.treu', { nom })}
          onClick={onTreu}
        >
          <Icona nom="xmark" />
        </button>
      </span>
      <Icona nom="sliders" className="dim" />
    </li>
  );
}

export function W13Rotacio() {
  const { t } = useTranslation('planificacio');
  const f = useFormat();
  const missatgeError = useMissatgeError();
  const navega = useNavigate();
  const { id: idParam } = useParams();
  const id = Number(idParam);
  const calendari = useCalendari(id);
  const rotacions = useRotacions(id);
  const desdeTorns = useMemo(() => avui().toISOString(), []);
  const finsTorns = useMemo(() => sumaDies(avui(), 90).toISOString(), []);
  const torns = useTorns(id, desdeTorns, finsTorns);
  const noms = useNoms();
  const genera = useGeneraRotacio();
  const [periodicitat, setPeriodicitat] = useState<Periodicitat>('SETMANAL');
  const [desde, setDesde] = useState(diaClau(avui()));
  const [fins, setFins] = useState(diaClau(sumaDies(avui(), 90)));
  const [senseFi, setSenseFi] = useState(false);
  const [diaInici, setDiaInici] = useState(1);
  const [horaCanvi, setHoraCanvi] = useState('08:00');
  const [llista, setLlista] = useState<number[]>([]);
  const [extres, setExtres] = useState<Map<number, string>>(new Map());
  const [inicialitzat, setInicialitzat] = useState(false);
  const [cerca, setCerca] = useState('');
  const cercaUsuaris = useCercaUsuaris(cerca);
  const [previ, setPrevi] = useState<ResultatRotacioDto | null>(null);

  // Els conflictes es calculen contra TOTS els torns existents dins el rang de la previsualització.
  const rang = useMemo(() => {
    if (!previ?.torns.length) return null;
    const inicis = previ.torns.map((x) => new Date(x.inici).getTime());
    const finals = previ.torns.map((x) => new Date(x.fi).getTime());
    return {
      desde: new Date(Math.min(...inicis)).toISOString(),
      fins: new Date(Math.max(...finals)).toISOString(),
    };
  }, [previ]);
  const existents = useTorns(rang ? id : NaN, rang?.desde ?? '', rang?.fins ?? '');

  if (!inicialitzat && !rotacions.isLoading && !torns.isLoading) {
    const r = rotacioActiva(rotacions.data);
    if (r) {
      setPeriodicitat(r.periodicitat);
      setDiaInici(r.diaInici);
      setHoraCanvi(r.horaCanvi.slice(0, 5));
      setLlista(r.participants);
    } else {
      setLlista(participants(torns.data ?? []));
    }
    setInicialitzat(true);
  }

  const nom = (pid: number) => extres.get(pid) ?? noms(pid);
  const sensors = useSensors(useSensor(PointerSensor, { activationConstraint: { distance: 4 } }));
  const enDeixar = (e: DragEndEvent) => {
    if (!e.over || e.active.id === e.over.id) return;
    const de = llista.indexOf(Number(e.active.id));
    const a = llista.indexOf(Number(e.over.id));
    if (de >= 0 && a >= 0) setLlista(arrayMove(llista, de, a));
  };

  const rotacio = (): RotacioDto => ({
    id: null,
    calendariId: id,
    periodicitat,
    diaInici,
    horaCanvi,
    participants: llista,
    desde,
    fins: senseFi ? null : fins,
    actiu: true,
  });

  const invalida = () => setPrevi(null);
  const conflictesServidor = previ?.conflictes;
  const conflictes =
    typeof conflictesServidor === 'number'
      ? conflictesServidor
      : Array.isArray(conflictesServidor)
        ? conflictesServidor.length
        : previ
          ? tornsSolapats(previ.torns, existents.data ?? []).length
          : 0;
  const calculantConflictes = conflictesServidor == null && !!rang && existents.isLoading;
  const potGenerar = llista.length > 0 && !!desde && (senseFi || fins >= desde);
  const generaPrevi = () =>
    genera.mutate({ rotacio: rotacio(), previsualitzar: true }, { onSuccess: setPrevi });

  return (
    <section className="pantalla" data-screen="W-13">
      <Toolbar
        titol={t('rotacio.titol', { nom: calendari.data?.nom ?? '' })}
        enrere={`/planificacio/calendaris/${id}`}
      >
        <Link className="btn btn--s" to={`/planificacio/calendaris/${id}`}>
          {t('comu.cancella')}
        </Link>
        <button
          type="button"
          className="btn btn--blue btn--s"
          disabled={!potGenerar || genera.isPending}
          onClick={generaPrevi}
        >
          <Icona nom="arrow-clockwise" />
          {t('rotacio.genera')}
        </button>
      </Toolbar>
      <div className="content rotacio">
        <div className="panel">
          <div className="panel__head">
            <h3>{t('rotacio.pas1')}</h3>
          </div>
          <div className="panel__body form">
            <Camp etiqueta={t('rotacio.periodicitat')}>
              <div className="segmented" style={{ alignSelf: 'flex-start' }}>
                {PERIODICITATS.map((p) => (
                  <button
                    key={p}
                    type="button"
                    aria-pressed={periodicitat === p}
                    onClick={() => {
                      setPeriodicitat(p);
                      invalida();
                    }}
                  >
                    {t(`periodicitat.${p}`)}
                  </button>
                ))}
              </div>
            </Camp>
            <div className="grid grid-2" style={{ gap: 12 }}>
              <Camp etiqueta={t('rotacio.desde')}>
                <input
                  className="input"
                  type="date"
                  value={desde}
                  onChange={(e) => {
                    setDesde(e.target.value);
                    invalida();
                  }}
                />
              </Camp>
              <Camp etiqueta={t('rotacio.fins')}>
                <input
                  className="input"
                  type="date"
                  value={fins}
                  disabled={senseFi}
                  onChange={(e) => {
                    setFins(e.target.value);
                    invalida();
                  }}
                />
              </Camp>
            </div>
            <label className="switch-row">
              <Icona nom="calendar" className="c-gray" />
              {t('rotacio.senseFi')}
              <input
                type="checkbox"
                className="switch"
                checked={senseFi}
                onChange={(e) => {
                  setSenseFi(e.target.checked);
                  invalida();
                }}
              />
            </label>
            <div className="camp">
              <span className="camp__label">{t('rotacio.diaInici')}</span>
              <div className="chips" role="group" aria-label={t('rotacio.diaInici')}>
                {[1, 2, 3, 4, 5, 6, 7].map((d) => (
                  <button
                    key={d}
                    type="button"
                    className="chip"
                    aria-pressed={diaInici === d}
                    onClick={() => {
                      setDiaInici(d);
                      invalida();
                    }}
                  >
                    {t(`diesLlargs.${d - 1}`)}
                  </button>
                ))}
              </div>
            </div>
            <Camp etiqueta={t('rotacio.horaCanvi')}>
              <input
                className="input"
                type="time"
                style={{ maxWidth: 160 }}
                value={horaCanvi}
                onChange={(e) => {
                  setHoraCanvi(e.target.value);
                  invalida();
                }}
              />
            </Camp>
          </div>
          <div className="panel__head" style={{ borderTop: '0.5px solid var(--separator)' }}>
            <h3>{t('rotacio.pas2', { count: llista.length })}</h3>
          </div>
          <div className="panel__body stack stack--s">
            <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={enDeixar}>
              <SortableContext items={llista} strategy={verticalListSortingStrategy}>
                <ol className="participants">
                  {llista.map((pid, i) => (
                    <Participant
                      key={pid}
                      id={pid}
                      nom={nom(pid)}
                      index={i}
                      total={llista.length}
                      onMou={(de, a) => {
                        setLlista(arrayMove(llista, de, a));
                        invalida();
                      }}
                      onTreu={() => {
                        setLlista(llista.filter((x) => x !== pid));
                        invalida();
                      }}
                    />
                  ))}
                </ol>
              </SortableContext>
            </DndContext>
            {llista.length === 0 ? (
              <div className="empty">
                <Icona nom="person-2" />
                {t('rotacio.senseParticipants')}
              </div>
            ) : null}
            <p className="t-caption dim" style={{ margin: 0 }}>
              {t('rotacio.ajudaOrdre')}
            </p>
            <label className="search" style={{ margin: 0, maxWidth: 320 }}>
              <Icona nom="magnifier" />
              <input
                type="search"
                placeholder={t('editor.cercaPersona')}
                aria-label={t('editor.cercaPersona')}
                value={cerca}
                onChange={(e) => setCerca(e.target.value)}
              />
            </label>
            {(cercaUsuaris.data ?? [])
              .filter((u) => !llista.includes(u.id))
              .map((u) => (
                <button
                  key={u.id}
                  type="button"
                  className="btn btn--ghost btn--xs"
                  style={{ alignSelf: 'flex-start' }}
                  onClick={() => {
                    setExtres(new Map(extres).set(u.id, nomUsuari(u)));
                    setLlista([...llista, u.id]);
                    setCerca('');
                    invalida();
                  }}
                >
                  <Icona nom="plus" />
                  {t('editor.afegeix', { nom: nomUsuari(u) })}
                </button>
              ))}
          </div>
        </div>
        <div className="panel">
          <div className="panel__head">
            <h3>{t('rotacio.previsualitzacio')}</h3>
          </div>
          <div className="panel__body">
            {genera.isError ? <p className="error-text">{missatgeError(genera.error)}</p> : null}
            {previ ? (
              <>
                <div className="previ-llista">
                  {previ.torns.slice(0, PREVI_VISIBLES).map((x, i) => (
                    <div className="hstack gap-8" key={`${x.inici}-${i}`}>
                      <Avatar nom={nom(x.titularId)} />
                      <div className="list-row__text">
                        <div className="list-row__label">
                          {t('editor.periode', {
                            desde: dataCurta(x.inici, f.idioma),
                            fins: dataCurta(x.fi, f.idioma),
                          })}
                        </div>
                        <div className="list-row__sub">
                          {t('rotacio.titularDe', { nom: nom(x.titularId) })}
                        </div>
                      </div>
                    </div>
                  ))}
                  {previ.torns.length > PREVI_VISIBLES ? (
                    <p className="t-caption dim" style={{ margin: 0 }}>
                      {t('rotacio.mes', { count: previ.torns.length - PREVI_VISIBLES })}
                    </p>
                  ) : null}
                </div>
                <hr className="hr" />
                <p className="t-footnote dim" style={{ margin: '0 0 6px' }}>
                  {t('rotacio.resum', { count: previ.torns.length })}
                </p>
                <div className="kv">
                  <span className="kv__k">{t('rotacio.tornsGenerats')}</span>
                  <span className="kv__v">{previ.torns.length}</span>
                </div>
                <div className="kv">
                  <span className="kv__k">{t('rotacio.conflictesDetectats')}</span>
                  <span
                    className={`kv__v ${conflictes ? 'c-red' : 'c-green'}`}
                    data-conflictes={conflictes}
                  >
                    {calculantConflictes
                      ? t('comu.carregant')
                      : conflictes
                        ? t('rotacio.conflictes', { count: conflictes })
                        : t('rotacio.cap')}
                  </span>
                </div>
                <button
                  type="button"
                  className="btn btn--blue btn--s btn--block mt-12"
                  disabled={genera.isPending}
                  onClick={() =>
                    genera.mutate(
                      { rotacio: rotacio(), previsualitzar: false },
                      { onSuccess: () => navega(`/planificacio/calendaris/${id}`) },
                    )
                  }
                >
                  <Icona nom="checkmark" />
                  {t('rotacio.confirma')}
                </button>
              </>
            ) : (
              <div className="empty">
                <Icona nom="calendar-clock" />
                {t('rotacio.ajudaPrevi')}
              </div>
            )}
          </div>
        </div>
      </div>
    </section>
  );
}
